import { type Filter } from "@/models"
import { css } from "vite-css-in-js"
import { ref } from "vue"
import { defineComponent, type ReactiveComponent } from "vue-utils"
import { useEditingSearchState } from "../state"
import ExportMarriageClaimsModal from "./ClaimsTable/ExportMarriageClaimsModal"
import SearchMarriageClaimsHeader from "./ClaimsTable/SearchMarriageClaimsHeader"
import ColumnManagerModal from "./Columns/ColumnManagerModal"
import QuickFilterModal from "./Filters/QuickFilterModal"
import SearchMarriageClaimFilterHeader from "./Filters/SearchMarriageClaimFilterHeader"

const layoutWithFilter = css`
	display: grid;
	grid-template-columns: 1fr 20rem;
	align-items: stretch;
	gap: 0.5rem;
	height: 100%;
	overflow: auto;
	flex: 1 1 0;

	@media (width < 1120px) {
		grid-template-columns: 1fr;
		height: 200%;
	}
`

const layoutWithoutFilter = css`
	display: grid;
	grid-template-columns: 1fr;
	align-items: stretch;
	gap: 0.75rem;
	height: 100%;
`

const MarriageClaimSearchContent: ReactiveComponent = () => {
	const state = useEditingSearchState()

	const managingColumns = ref<boolean>(false)
	const managingFilters = ref<boolean>(true)
	const advancedFilters = ref<boolean>(true)
	const creatingFilter = ref<boolean>(false)
	const editingFilterIndex = ref<number | null>(null)
	const choosingExportType = ref<boolean>(false)

	function saveFilter(savedFilter: Filter) {
		if (editingFilterIndex.value === null) {
			state.createFilter(savedFilter)
		} else {
			state.updateFilter(savedFilter, editingFilterIndex.value)
		}
		closeFilterModal()
	}

	function closeFilterModal() {
		creatingFilter.value = false
		editingFilterIndex.value = null
	}

	return () => (
		<div class={managingFilters.value ? layoutWithFilter : layoutWithoutFilter}>
			<SearchMarriageClaimsHeader
				managingFilters={managingFilters.value}
				openManageColumns={() => (managingColumns.value = true)}
				openManageFilters={() => (managingFilters.value = true)}
				openChooseExportType={() => (choosingExportType.value = true)}
			/>

			{managingFilters.value && (
				<SearchMarriageClaimFilterHeader
					advanced={advancedFilters.value}
					onTypeChange={(advanced) => (advancedFilters.value = advanced)}
					onCreatingFilter={() => (creatingFilter.value = true)}
					onFilterEdited={(filter) => (editingFilterIndex.value = state.filters.indexOf(filter))}
					onClose={() => (managingFilters.value = false)}
				/>
			)}
			{(creatingFilter.value || editingFilterIndex.value !== null) && (
				<QuickFilterModal
					selectedFilter={editingFilterIndex.value === null ? null : state.filters[editingFilterIndex.value]}
					onSaved={saveFilter}
					onCancelled={closeFilterModal}
				/>
			)}
			{managingColumns.value && (
				<ColumnManagerModal
					columns={state.allColumns}
					onCancelled={() => (managingColumns.value = false)}
					onSave={(columns) => {
						state.allColumns = columns
						managingColumns.value = false
					}}
				/>
			)}
			{choosingExportType.value && (
				<ExportMarriageClaimsModal
					onCancelled={() => (choosingExportType.value = false)}
					onChosen={(type) => {
						void state.executeExport(type)
						choosingExportType.value = false
					}}
				/>
			)}
		</div>
	)
}

export default defineComponent(MarriageClaimSearchContent)
